import ENS_PUBLIC_RESOLVER_ABI from 'abis/ens-public-resolver.json'
import ENS_ABI from 'abis/ens-registrar.json'
import { WAGMI_REFETCH_POLICY_NEVER } from 'config/RefetchPolicies'
import { ENS_REGISTRAR_ADDRESSES } from 'constants/addresses'
import { useMemo } from 'react'
import type { Abi, Address } from 'viem'
import { namehash } from 'viem/ens'
import { useReadContract } from 'wagmi'

import isZero from '../utils/isZero'
import { useConnection } from './useConnection'

/**
 * Does a lookup for an ENS name to find its contenthash.
 */
export default function useENSContentHash(ensName?: string | null): { loading: boolean; contenthash: string | null } {
  const { chainId } = useConnection()
  const ensNodeArgument = useMemo(() => {
    if (!ensName) return undefined
    try {
      return namehash(ensName) as Address
    } catch {
      return undefined
    }
  }, [ensName])

  const registrarAddress = ENS_REGISTRAR_ADDRESSES[chainId]
  // Step 1: Get resolver address from ENS registrar
  const { data: resolverAddressResult, isLoading: resolverLoading } = useReadContract({
    address: registrarAddress,
    abi: ENS_ABI as Abi,
    functionName: 'resolver',
    args: ensNodeArgument ? [ensNodeArgument] : undefined,
    query: {
      ...WAGMI_REFETCH_POLICY_NEVER,
      enabled: !!ensNodeArgument && !!registrarAddress,
    },
  })

  const resolverAddress = useMemo(() => {
    if (!resolverAddressResult || isZero(resolverAddressResult as string)) return undefined
    return resolverAddressResult as Address
  }, [resolverAddressResult])

  // Step 2: Get contenthash from resolver
  const { data: contenthashResult, isLoading: contenthashLoading } = useReadContract({
    address: resolverAddress,
    abi: ENS_PUBLIC_RESOLVER_ABI as Abi,
    functionName: 'contenthash',
    args: ensNodeArgument ? [ensNodeArgument] : undefined,
    query: {
      ...WAGMI_REFETCH_POLICY_NEVER,
      enabled: !!resolverAddress && !!ensNodeArgument,
    },
  })

  return useMemo(
    () => ({
      contenthash: (contenthashResult as string) ?? null,
      loading: resolverLoading || contenthashLoading,
    }),
    [contenthashResult, resolverLoading, contenthashLoading]
  )
}
